import { styled, TextField, Paper } from '@mui/material';
import { Theme } from '@mui/material/styles';
import Autocomplete from 'components/FormElements/AppAutocomplete';
import { useAdvisersLeanResponse } from 'hooks/data/adviser/useAdvisers';
import { useGetRole } from 'hooks/ui/useGetRole';
import { Api } from 'further-types/adviser';

type Adviser = Api.ListAdviserLeanResponse['advisers'][number];

export const defaultLabelRenderer = (option: Adviser): string => {
  return `${option.fullName ?? ''} (${option.email})${
    option.organisation?.name ? ` [${option.organisation.name}]` : ''
  }`;
};

type Props = {
  adviserId?: string | null;
  onChange: (adviser: Adviser | null) => void;
  placeholder?: string;
  fullWidth?: boolean;
  labelRenderer?: (option: Adviser) => string;
  id?: string;
  hiddenAdviserIdsFromOptions?: Array<string>;
  disabled?: boolean;
  refetchOnWindowFocus?: boolean;
};

const Container = styled('div')(
  ({
    fullWidth,
    disabled,
    theme,
  }: {
    fullWidth: boolean;
    disabled: boolean;
    theme: Theme;
  }) => ({
    maxWidth: fullWidth ? '100%' : '300px',
    '& > div': {
      backgroundColor: disabled ? theme.palette.grey[100] : 'transparent',
    },
    '& .MuiAutocomplete-endAdornment': {
      backgroundColor: disabled ? 'transparent' : 'inherit',
    },
  }),
);

const StyledPaper = styled(Paper)(() => ({
  minWidth: '400px',
  maxWidth: '600px',
}));

const AdviserSelect: React.FC<Props> = ({
  adviserId,
  onChange,
  fullWidth,
  placeholder,
  labelRenderer,
  id,
  hiddenAdviserIdsFromOptions = [],
  disabled = false,
  refetchOnWindowFocus,
}) => {
  const { firmId } = useGetRole();
  const { data, isLoading } = useAdvisersLeanResponse(
    {
      firmId,
      order: 'asc',
      orderBy: 'fullName',
    },
    refetchOnWindowFocus,
  );

  const advisers = data?.advisers.filter(
    (adviser) => !hiddenAdviserIdsFromOptions.includes(adviser._id.toString()),
  );

  const adviser = data?.advisers.find(
    (adviser) => adviser._id.toString() === adviserId,
  );

  return (
    <Container fullWidth={fullWidth} disabled={disabled}>
      <Autocomplete
        id={id}
        name={id}
        disabled={disabled}
        loading={isLoading}
        options={advisers ?? []}
        getOptionLabel={labelRenderer ?? defaultLabelRenderer}
        getOptionKey={(option) => option._id}
        filterSelectedOptions
        value={adviser ?? null}
        onChange={(_event, adviser) => {
          onChange?.(adviser);
        }}
        slotProps={{
          paper: {
            component: StyledPaper,
          },
        }}
        renderInput={(params) => (
          <TextField {...params} variant="outlined" placeholder={placeholder} />
        )}
      />
    </Container>
  );
};

export default AdviserSelect;
